import { useState } from 'react';
import { i18n } from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { TooltipProvider } from '@monksflow/monks-ui';
import { PageTransition } from '@/shared/ui/animations';
import { BreadcrumbPageHeader, ListPageBody, ListPageLayout } from '@/shared/ui/layout';
import { AboutPageDialog } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { TaxonomyTabs } from '../TaxonomyList/components/TaxonomyTabs/TaxonomyTabs';

/** Per-tab subtitle. Swapping text does not remount the header. */
function describeTab(pathname: string): string {
  if (pathname.endsWith('/categories'))
    return i18n.t('knowledge:taxonomy.taxonomyLayout.groupsOrganiseYourFieldDefinitions');
  if (pathname.endsWith('/fields'))
    return i18n.t('knowledge:taxonomy.taxonomyLayout.reusableFieldDefinitionsYourSchemas');
  if (pathname.endsWith('/lists'))
    return i18n.t('knowledge:taxonomy.taxonomyLayout.reusableVocabulariesSharedAcrossSchemas');
  return i18n.t('knowledge:taxonomy.taxonomyLayout.defineVersionNamingSchemasKeep');
}

/**
 * Shared shell for the four taxonomy tabs.
 *
 * The header, the tab bar and the content frame are mounted ONCE here and the
 * active tab renders into the `<Outlet />`. Because these stay mounted across
 * tab changes, switching tabs re-renders only the frame below — no page-level
 * remount, and the header's entrance animation runs once on arrival instead of
 * replaying on every click.
 *
 * The per-tab description moves into the header's info dialog rather than being
 * dropped: the breadcrumb header carries no description slot, and this is where
 * every other migrated Knowledge Hub page puts the same copy. Returning to the
 * hub is the breadcrumb's job now, so the explicit back button is gone.
 */
export function TaxonomyLayout() {
  const { t } = useTranslation('knowledge');
  const { pathname } = useLocation();
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  return (
    <TooltipProvider>
      <ListPageLayout>
        <PageTransition variant="slideUp">
          <BreadcrumbPageHeader
            trail={[
              {
                label: t('taxonomy.taxonomyLayout.knowledge'),
                href: ROUTES.KNOWLEDGE_HUB,
              },
            ]}
            page={t('taxonomy.taxonomyLayout.taxonomyAdministration')}
            info={{ onClick: () => setIsAboutOpen(true) }}
          />
        </PageTransition>

        <PageTransition variant="slideUp" delay={0.05}>
          <TaxonomyTabs />
        </PageTransition>

        <ListPageBody>
          <Outlet />
        </ListPageBody>
      </ListPageLayout>

      <AboutPageDialog
        open={isAboutOpen}
        onOpenChange={setIsAboutOpen}
        title={t('taxonomy.taxonomyLayout.taxonomyAdministration')}
        description={describeTab(pathname)}
        rows={[]}
      />
    </TooltipProvider>
  );
}

TaxonomyLayout.displayName = 'TaxonomyLayout';
